import React, { useEffect, useRef, useCallback, memo } from 'react';

import { subscribeErrorTooltip } from '../../utils/errorTooltipEmitter';

/**
 * Tooltip component for showing error messages.
 */
const TooltipComponent = ({ name, content, children }) => {
  const spanRef = useRef(null);
  const tooltipId = name.replace(/\./g, '_');

  const createElement = useCallback(
    text => {
      const div = document.createElement('div');

      div.id = tooltipId;
      div.className = 'alert alert-danger alert-dismissible input-custom-alert hide';
      div.addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();
      });

      div.innerHTML = `<span class="text">${text}</span><button type="button" class="close" aria-label="Close"><span aria-hidden="true">×</span></button>`;
      div.querySelector('button').addEventListener('click', event => {
        event.stopPropagation();
        div.remove();
      });
      document.body.append(div);
    },
    [tooltipId],
  );

  const hideElement = useCallback(() => {
    const element = document.getElementById(tooltipId);

    if (element) element.remove();
  }, [tooltipId]);

  const updatePosition = useCallback(() => {
    const element = document.getElementById(tooltipId);

    if (spanRef.current && element) {
      const rect = spanRef.current.getBoundingClientRect();

      element.style.position = 'absolute';
      element.style.top = `${rect.top + rect.height}px`;
      element.style.left = `${rect.left}px`;
      element.classList.remove('hide');
    }
  }, [tooltipId]);

  const showElement = useCallback(
    text => {
      createElement(text);
      updatePosition();
    },
    [createElement, updatePosition],
  );

  const shouldShowTooltip = useCallback(
    tooltipContent => {
      if (tooltipContent) {
        showElement(tooltipContent);
      } else {
        hideElement();
      }
    },
    [showElement, hideElement],
  );

  useEffect(() => {
    document.addEventListener('click', hideElement);

    const unsubscribe = subscribeErrorTooltip(errors => {
      const tooltipContent = (errors || {})[name];

      shouldShowTooltip(tooltipContent);
    });

    shouldShowTooltip(content);

    return () => {
      document.removeEventListener('click', hideElement);
      unsubscribe();
    };
  }, [name, content, shouldShowTooltip, hideElement]);

  return (
    <span ref={spanRef} data-toggle="tooltip">
      {children}
    </span>
  );
};

export const Tooltip = memo(TooltipComponent);
